import {Injectable} from '@angular/core';
import {ApiClientService} from './api-client.service';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {DirectoryItemComponent} from '../views/directory-item/directory-item.component';
import {AccountPermissionsService} from './account-permissions.service';
import {DirectoryService} from 'app/services/directory.service';
import {map, tap} from 'rxjs/operators';

declare const toastr: any;

@Injectable()
export abstract class ApiService {

    constructor(public api: ApiClientService,
                public route: ActivatedRoute,
                public router: Router,
                public account: AccountPermissionsService) {
    }

    abstract getItems(suiteId: number): Observable<any>;

    abstract getItemId(parent: DirectoryItemComponent): Observable<number>;

    abstract getItem(id: number): Observable<any>;

    abstract updateItem(id: number, data: any): Observable<any>;

    abstract deleteItem(id: number): Observable<any>;

    abstract updateView(parent: DirectoryItemComponent, isRedirect: boolean): void;

    protected prepareItemId(urlKey: string, listKey: string, parent: DirectoryItemComponent): Observable<number> {
        return this.route.params.map((params: Params) => {
            const id = parseInt(params['id']);

            if (!id) {
                if (parent[listKey].length) {
                    this.router.navigateByUrl('/directory/item/' + parent.suiteId + `/${urlKey}/` + parent[listKey][0].id, {queryParams: parent.directoryService.urlService.queryParams});
                } else {
                    this.getItems(parent.suiteId).subscribe((res) => {
                        if (res.entities.length) {
                            parent[listKey] = res.entities;

                            const id = res.entities[0].id;
                            this.router.navigateByUrl('/directory/item/' + parent.suiteId + `/${urlKey}/` + id, {queryParams: parent.directoryService.urlService.queryParams});
                        }
                    });
                }
            } else {
                if (!parent[listKey].length) {
                    this.getItems(parent.suiteId).subscribe((res) => {
                        if (res.entities.length) {
                            parent[listKey] = res.entities;
                        }
                    });
                }

                return id;
            }
        });
    }

    protected updateParentList(suiteId: number, listKey: string, parent: DirectoryItemComponent): Observable<any> {
        parent[listKey] = [];

        return this.getItems(suiteId).map((res) => {
            if (res.entities.length) {
                parent[listKey] = res.entities;
            }
        });
    }

}

@Injectable()
export class ApiResidentService extends ApiService {

    constructor(public api: ApiClientService,
                public route: ActivatedRoute,
                public router: Router,
                public account: AccountPermissionsService,
                public directoryService: DirectoryService) {
        super(api, route, router, account);
    }

    getItems(suiteId: number): Observable<any> {
        const url = `/v1/residents?suite_id=${suiteId}&expand=suiteUser${this.account.authService.urlService.queryParams['show_moved_out'] ? '&show_moved_out=true' : ''}`; // &order-by=suiteUser.role|asc

        return this.api.getJson('domainBD', url)
            .pipe(
                map((r) => {
                    return {
                        entities: r.entities.sort((entityA, entityB) => {
                            const a = entityA.suiteUser[0] ? entityA.suiteUser[0].role : '';
                            const b = entityB.suiteUser[0] ? entityB.suiteUser[0].role : '';
                            if (a > b) {
                                return 1;
                            }
                            if (a < b) {
                                return -1;
                            }
                            return 0;
                        }),
                        total: r.total
                    };
                }),
                tap(
                    () => {
                    },
                    (error) => this.api.errorHandler(error, 'Failed get Residents')
                )
            );
    }

    getItemId(parent: DirectoryItemComponent): Observable<number> {
        return this.prepareItemId('residents', 'residentsList', parent);
    }

    getItem(id: number): Observable<any> {
        const url = `/v1/residents/${id}?expand=phones,addresses,groups.group,suiteUser,suiteUser.suite,suiteUser.building,suiteUser.building.timeZone,consent,suiteUser.suite.buzzFields,suiteUser.suite.keyCodeFields,suiteUser.suite.keyFobFields,buzzFields,keyCodeFields,keyFobFields,emergencyContacts,suiteUser.suite.vehicles,suiteUser.suite.pet,suiteUser.suite.pet.petType${this.account.authService.urlService.queryParams['show_moved_out'] ? '&show_moved_out=true' : ''}`;

        return this.api.get('domainBD', url).map(data => {
            const responseBody = JSON.parse(data._body);
            responseBody.building = responseBody.suiteUser[0].building;
            this.directoryService.getCorporationSettings(responseBody.building.corporationNumber);
            if (this.account.canRead('sms'))
                this.directoryService.getCorporationSmsSettings(responseBody.building.corporationNumber);
            this.account.addEntitiesPermissions([responseBody], 'bldn');
            return responseBody;
        });
    }

    updateItem(id: number, data: any): Observable<any> {
        const url = `/v1/suite-users/${id}`;
        const body = this.prepareData(data);

        return this.api.patch('domainBD', url, body).map((res) => {
            toastr.success('Resident was updated!');

            return res;
        });
    }

    prepareData(data: any): any {
        const result: any = {
            user: {},
        };

        if (data.groups) {
            result.user.groups = data.groups.map(g => ({group: +g.id}));
        }

        if (data.phones) {
            result.user.phones = [];
            data.phones.forEach((phone) => {
                if (phone.phone) {
                    result.user.phones.push({
                        isPrimaryPhone: +phone.isPrimaryPhone,
                        phone: phone.phone,
                        phoneType: phone.phoneType,
                        user: phone.user
                    });
                }
            });
        }

        if (data.addresses) {
            result.user.addresses = [];
            data.addresses.forEach((address) => {
                if (address.address) {
                    result.user.addresses.push({
                        address: address.address,
                        city: address.city,
                        country: address.country,
                        isPrimaryAddress: +address.isPrimaryAddress,
                        postalCode: address.postalCode,
                        state: address.state,
                        user: address.user
                    });
                }
            });
        }

        result.occupancyDate = data.occupancyDate;
        result.moveOutDate = data.moveOutDate;

        if (data.alternativeEmails) {
            result.user.alternativeEmails = [];
            data.alternativeEmails.forEach((item) => {
                result.user.alternativeEmails.push(
                    {email: item.email}
                );
            });
        }

        createValueIsSet('email');
        createValueIsSet('isValidEmail');
        createValueIsSet('fullName');
        createValueIsSet('dateOfBirth');
        createValueIsSet('emergencyContact');
        createValueIsSet('emergencyContacts');
        createValueIsSet('emergencyAssistantNotes');
        createValueIsSet('emailWaiverSigned');
        createValueIsSet('parcelWaiverSigned');
        createValueIsSet('keyWaiverSigned');
        createValueIsSet('note');
        createValueIsSet('photo');
        createValueIsSet('ownerInformationFormReceived');
        createValueIsSet('leaseReceived');
        createValueIsSet('tenantInformationFormReceived');
        createValueIsSet('primaryLeaseHolder');
        createValueIsSet('leaseStartDate');
        createValueIsSet('disabilities');

        if (data.role) {
            result.role = data.role;
        }
        return result;

        function createValueIsSet(key) {
            result.user[key] = data[key];
        }
    }

    deleteItem(id: number): Observable<any> {
        const url = `/v1/suite-users/${id}`;

        return this.api.remove('domainBD', url).map((res) => {
            toastr.success('This resident has now been marked as moved out and therefore will no longer have access to their account.');

            return res;
        }, (error) => this.api.errorHandler(error, 'Moved out resident failed'));
    }

    updateView(parent: DirectoryItemComponent, isRedirect: boolean = true): void {
        if (!parent.residentsList.length) {
            isRedirect = true;
        }

        this.updateParentList(parent.suiteId, 'residentsList', parent).subscribe(() => {
            if (isRedirect) {
                const url = `/directory/item/${parent.suiteId}/residents`;
                const isCurrentUrl = this.router.url === url;

                if (isCurrentUrl) this.getItemId(parent).subscribe();
                else this.router.navigate([url]);
            }
        });
    }

}

@Injectable()
export class ApiLockersService extends ApiService {

    constructor(public api: ApiClientService,
                public route: ActivatedRoute,
                public router: Router,
                public account: AccountPermissionsService) {
        super(api, route, router, account);
    }

    getItems(suiteId: number): Observable<any> {
        const url = `/v1/locker?suite=${suiteId}`;

        return this.api.get('domainBD', url).map(res => JSON.parse(res._body),
            (error) => this.api.errorHandler(error, 'Failed get Residents'));
    }

    getItemId(parent: DirectoryItemComponent): Observable<number> {
        return this.prepareItemId('lockers', 'lockersList', parent);
    }

    getItem(id: number) {
        const url = `/v1/locker/${id}`;

        return this.api.get('domainBD', url).map(data => {
            const responseBody = JSON.parse(data._body);
            responseBody.formFields = {
                rentedToBuilding: {editMode: false},
                rentedToSuite: {editMode: false},
                rentedToSuiteUser: {editMode: false}
            };
            if (responseBody.extendedRentedTo)
                responseBody.extendedRentedTo = {
                    building: responseBody.extendedRentedTo.building ? responseBody.extendedRentedTo.building.id : undefined,
                    buildingName: responseBody.extendedRentedTo.building ? responseBody.extendedRentedTo.building.name : undefined,
                    suite: responseBody.extendedRentedTo.suite ? (responseBody.extendedRentedTo.suite.id || 0) : undefined,
                    suiteNumber: responseBody.extendedRentedTo.suite ? (responseBody.extendedRentedTo.suite.suiteNumber) : undefined,
                    suiteUser: responseBody.extendedRentedTo.suiteUser ? (responseBody.extendedRentedTo.suiteUser.id || 0) : undefined,
                    suiteUserName: responseBody.extendedRentedTo.suiteUser ? (responseBody.extendedRentedTo.suiteUser.user.fullName) : undefined,
                };
            this.account.addEntitiesPermissions([responseBody], 'bldn');
            return responseBody;
        });
    }

    updateItem(id: number, data: any) {
        const url = `/v1/locker/${id}`;

        return this.api.patch('domainBD', url, this.prepareData(data)).map((res) => {
            toastr.success('Locker was updated!');

            return res;
        });
    }

    prepareData(data: any): any {
        const result: any = {};

        createValueIsSet('lockerKeyCode');
        createValueIsSet('legalDescription');
        createValueIsSet('rentedOut');
        createValueIsSet('rentedTo');
        createValueIsSet('extendedRentedTo');
        createValueIsSet('note');
        createValueIsSet('level');
        createValueIsSet('roomNumber');

        return result;

        function createValueIsSet(key) {
            result[key] = data[key];
        }
    }

    deleteItem(id: number): Observable<any> {
        const url = `/v1/locker/${id}`;
        return this.api.remove('domainBD', url).map((res) => {
            toastr.success('Locker was removed!');

            return res;
        }, (error) => this.api.errorHandler(error, 'Delete Locker failed'));
    }

    updateView(parent: DirectoryItemComponent, isRedirect: boolean = true): void {
        if (!parent.lockersList.length) {
            isRedirect = true;
        }

        this.updateParentList(parent.suiteId, 'lockersList', parent).subscribe(() => {
            if (isRedirect) {
                const url = `/directory/item/${parent.suiteId}/lockers`;
                const isCurrentUrl = this.router.url === url;

                if (isCurrentUrl) {
                    this.getItemId(parent).subscribe();
                } else {
                    this.router.navigate([url]);
                }
            }
        });
    }

}

@Injectable()
export class ApiParkingSpotsService extends ApiService {

    constructor(public api: ApiClientService,
                public route: ActivatedRoute,
                public router: Router,
                public account: AccountPermissionsService) {
        super(api, route, router, account);
    }

    getItems(suiteId: number): Observable<any> {
        const url = `/v1/parking?suite=${suiteId}`;

        return this.api.get('domainBD', url).map(res => JSON.parse(res._body),
            (error) => this.api.errorHandler(error, 'Failed get Parking'));
    }

    getItemId(parent: DirectoryItemComponent): Observable<number> {
        return this.prepareItemId('parking-spots', 'parkingList', parent);
    }

    getItem(id: number) {
        const url = `/v1/parking/${id}?expand=suite,building`;

        return this.api.get('domainBD', url).map(data => {
            const responseBody = JSON.parse(data._body);
            responseBody.formFields = {
                rentedToBuilding: {editMode: false},
                rentedToSuite: {editMode: false},
                rentedToSuiteUser: {editMode: false}
            };
            if (responseBody.extendedRentedTo)
                responseBody.extendedRentedTo = {
                    building: responseBody.extendedRentedTo.building ? responseBody.extendedRentedTo.building.id : undefined,
                    buildingName: responseBody.extendedRentedTo.building ? responseBody.extendedRentedTo.building.name : undefined,
                    suite: responseBody.extendedRentedTo.suite ? (responseBody.extendedRentedTo.suite.id || 0) : undefined,
                    suiteNumber: responseBody.extendedRentedTo.suite ? (responseBody.extendedRentedTo.suite.suiteNumber) : undefined,
                    suiteUser: responseBody.extendedRentedTo.suiteUser ? (responseBody.extendedRentedTo.suiteUser.id || 0) : undefined,
                    suiteUserName: responseBody.extendedRentedTo.suiteUser ? (responseBody.extendedRentedTo.suiteUser.user.fullName) : undefined,
                };
            this.account.addEntitiesPermissions([responseBody], 'bldn');
            return responseBody;
        });
    }

    updateItem(id: number, data: any) {
        const url = `/v1/parking/${id}`;

        return this.api.patch('domainBD', url, this.prepareData(data)).map((res) => {
            toastr.success('Parking Spot was updated!');

            return res;
        });
    }

    prepareData(data: any): any {
        const result: any = {};

        createValueIsSet('parkingNumber');
        createValueIsSet('level');
        createValueIsSet('rentedOut');
        createValueIsSet('rentedTo');
        createValueIsSet('extendedRentedTo');
        createValueIsSet('garageRemote');
        createValueIsSet('handicap');
        createValueIsSet('legalDescription');
        createValueIsSet('note');

        return result;

        function createValueIsSet(key) {
            result[key] = data[key];
        }
    }

    deleteItem(id: number): Observable<any> {
        const url = `/v1/parking/${id}`;
        return this.api.remove('domainBD', url).map((res) => {
            toastr.success('Parking Spot was removed!');

            return res;
        }, (error) => this.api.errorHandler(error, 'Delete Parking Spot failed'));
    }

    updateView(parent: DirectoryItemComponent, isRedirect: boolean = true): void {
        if (!parent.parkingList.length) {
            isRedirect = true;
        }

        this.updateParentList(parent.suiteId, 'parkingList', parent).subscribe(() => {
            if (isRedirect) {
                const url = `/directory/item/${parent.suiteId}/parking-spots`;
                const isCurrentUrl = this.router.url === url;

                if (isCurrentUrl) this.getItemId(parent).subscribe();
                else this.router.navigate([url]);
            }
        });
    }

}

@Injectable()
export class ApiVehiclesService extends ApiService {

    constructor(public api: ApiClientService,
                public route: ActivatedRoute,
                public router: Router,
                public account: AccountPermissionsService,
                public directoryService: DirectoryService) {
        super(api, route, router, account);
    }

    getItems(suiteId: number): Observable<any> {
        const url = `/v1/vehicle?suite_id=${suiteId}${this.account.authService.urlService.queryParams['show_moved_out'] ? '&show_moved_out=true' : ''}`;

        return this.api.get('domainBD', url).map(res => JSON.parse(res._body),
            (error) => this.api.errorHandler(error, 'Failed get Vehicle'));
    }

    getItemId(parent: DirectoryItemComponent): Observable<number> {
        return this.prepareItemId('vehicles', 'vehicleList', parent);
    }

    getItem(id: number) {
        const url = `/v1/vehicle/${id}?expand=owner.suiteUser`;

        return this.api.get('domainBD', url).map(data => {
            const responseBody = JSON.parse(data._body);
            this.account.addEntitiesPermissions([responseBody], 'bldn');
            return responseBody;
        });
    }

    updateItem(id: number, data: any) {
        const url = `/v1/vehicle/${id}`;

        const body = this.prepareData(data);

        return this.api.patch('domainBD', url, body).map((res) => {
            toastr.success('Vehicle was updated!');

            return res;
        });
    }

    prepareData(data: any): any {
        const result: any = {};

        if (data.owner) {
            if (Number.isInteger(data.owner)) {
                result['owner'] = data.owner;
            } else {
                result['ownerName'] = data.owner;
            }
        }

        createValueIsSet('brand');
        createValueIsSet('licensePlate');
        createValueIsSet('permitNumber');
        createValueIsSet('model');
        createValueIsSet('note');
        createValueIsSet('ownerName');
        createValueIsSet('year');
        createValueIsSet('moveOutDate');
        createValueIsSet('color');

        return result;

        function createValueIsSet(key) {
            result[key] = data[key];
        }
    }

    deleteItem(id: number): Observable<any> {
        const url = `/v1/vehicle/${id}`;
        return this.api.remove('domainBD', url).map((res) => {
            toastr.success('Vehicle was removed!');

            return res;
        }, (error) => this.api.errorHandler(error, 'Delete Vehicle failed'));
    }

    public getOwners(suiteId: number): Observable<any> {
        const url = `/v1/residents?suite_id=${suiteId}${this.account.authService.urlService.queryParams['show_moved_out'] ? '&show_moved_out=true' : ''}`;

        return this.api.get('domainBD', url).map(res => JSON.parse(res._body),
            (error) => this.api.errorHandler(error, 'Failed get Owners'));
    }

    updateView(parent: DirectoryItemComponent, isRedirect: boolean = true): void {
        if (!parent.vehicleList.length) {
            isRedirect = true;
        }

        this.updateParentList(parent.suiteId, 'vehicleList', parent).subscribe(() => {
            if (isRedirect) {
                const url = `/directory/item/${parent.suiteId}/vehicles`;
                const isCurrentUrl = this.router.url === url;

                if (isCurrentUrl) this.getItemId(parent).subscribe();
                else this.router.navigate([url]);
            }
        });
    }

}

@Injectable()
export class ApiPetsItemService extends ApiService {

    constructor(public api: ApiClientService,
                public route: ActivatedRoute,
                public router: Router,
                public account: AccountPermissionsService,
                public directoryService: DirectoryService) {
        super(api, route, router, account);
    }

    getItems(suiteId: number): Observable<any> {
        const url = `/v1/pet?suite=${suiteId}${this.account.authService.urlService.queryParams['show_moved_out'] ? '&show_moved_out=true' : ''}`;

        return this.api.get('domainBD', url).map(res => JSON.parse(res._body),
            (error) => this.api.errorHandler(error, 'Failed get Suite'));
    }

    getItemId(parent: DirectoryItemComponent): Observable<number> {
        return this.prepareItemId('pets', 'petsList', parent);
    }

    getItem(id: number): Observable<any> {
        const url = `/v1/pet/${id}`;

        return this.api.get('domainBD', url).map(res => {
                const responseBody = JSON.parse(res._body);
                this.account.addEntitiesPermissions([responseBody], 'bldn');
                return responseBody;
            },
            (error) => this.api.errorHandler(error, 'Failed get Suite'));
    }

    updateItem(id: number, data: any) {
        const url = `/v1/pet/${id}`;

        const body = this.prepareData(data);

        return this.api.patch('domainBD', url, body).map((res) => {
            toastr.success('Pet was updated!');

            return res;
        });
    }

    prepareData(data: any): any {
        const result: any = {};

        createValueIsSet('name');
        createValueIsSet('petType');
        createValueIsSet('weight');
        createValueIsSet('height');
        createValueIsSet('dateOfBirth');
        createValueIsSet('moveOutDate');
        createValueIsSet('breed');
        createValueIsSet('note');

        return result;

        function createValueIsSet(key) {
            result[key] = data[key];
        }
    }

    deleteItem(id: number): Observable<any> {
        const url = `/v1/pet/${id}`;
        return this.api.remove('domainBD', url).map((res) => {
            toastr.success('Pet was removed!');

            return res;
        }, (error) => this.api.errorHandler(error, 'Delete Pet failed'));
    }

    public getPetTypes(): Observable<any> {
        const url = `/v1/pet_type.json?per-page=1000&order-by=name|asc`;

        return this.api.get('domainBD', url).map(res => JSON.parse(res._body),
            (error) => this.api.errorHandler(error, 'Failed get Suite'));
    }

    updateView(parent: DirectoryItemComponent, isRedirect: boolean = true): void {
        if (!parent.petsList.length) {
            isRedirect = true;
        }

        this.updateParentList(parent.suiteId, 'petsList', parent).subscribe(() => {
            if (isRedirect) {
                const url = `/directory/item/${parent.suiteId}/pets`;
                const isCurrentUrl = this.router.url === url;

                if (isCurrentUrl) this.getItemId(parent).subscribe();
                else this.router.navigate([url]);
            }
        });
    }

}

@Injectable()
export class ApiBikeRacksService extends ApiService {

    constructor(public api: ApiClientService,
                public route: ActivatedRoute,
                public router: Router,
                public account: AccountPermissionsService) {
        super(api, route, router, account);
    }

    getItems(suiteId: number): Observable<any> {
        const url = `/v1/bike_rack?suite=${suiteId}`;

        return this.api.get('domainBD', url).map(res => JSON.parse(res._body),
            (error) => this.api.errorHandler(error, 'Failed get Suite'));
    }

    getItemId(parent: DirectoryItemComponent): Observable<number> {
        return this.prepareItemId('bike-racks', 'bikeRacksList', parent);
    }

    getItem(id: number): Observable<any> {
        const url = `/v1/bike_rack/${id}`;

        return this.api.get('domainBD', url).map(res => {
                const responseBody = JSON.parse(res._body);
                responseBody.formFields = {
                    rentedToBuilding: {editMode: false},
                    rentedToSuite: {editMode: false},
                    rentedToSuiteUser: {editMode: false}
                };
                if (responseBody.extendedRentedTo)
                    responseBody.extendedRentedTo = {
                        building: responseBody.extendedRentedTo.building ? responseBody.extendedRentedTo.building.id : undefined,
                        buildingName: responseBody.extendedRentedTo.building ? responseBody.extendedRentedTo.building.name : undefined,
                        suite: responseBody.extendedRentedTo.suite ? (responseBody.extendedRentedTo.suite.id || 0) : undefined,
                        suiteNumber: responseBody.extendedRentedTo.suite ? (responseBody.extendedRentedTo.suite.suiteNumber) : undefined,
                        suiteUser: responseBody.extendedRentedTo.suiteUser ? (responseBody.extendedRentedTo.suiteUser.id || 0) : undefined,
                        suiteUserName: responseBody.extendedRentedTo.suiteUser ? (responseBody.extendedRentedTo.suiteUser.user.fullName) : undefined,
                    };
                this.account.addEntitiesPermissions([responseBody], 'bldn');
                return responseBody;
            },
            (error) => this.api.errorHandler(error, 'Failed get Suite'));
    }

    updateItem(id: number, data: any) {
        const url = `/v1/bike_rack/${id}`;

        const body = this.prepareData(data);

        return this.api.patch('domainBD', url, body).map((res) => {
            toastr.success('Bike Rack was updated!');

            return res;
        });
    }

    prepareData(data: any): any {
        const result: any = {};

        createValueIsSet('rackNumber');
        createValueIsSet('rentedOut');
        createValueIsSet('rentedTo');
        createValueIsSet('extendedRentedTo');
        createValueIsSet('note');

        return result;

        function createValueIsSet(key) {
            result[key] = data[key];
        }
    }

    deleteItem(id: number): Observable<any> {
        const url = `/v1/bike_rack/${id}`;
        return this.api.remove('domainBD', url).map((res) => {
            toastr.success('Bike rack was removed!');

            return res;
        }, (error) => this.api.errorHandler(error, 'Delete Bike rack failed'));
    }

    updateView(parent: DirectoryItemComponent, isRedirect: boolean = true): void {
        if (!parent.bikeRacksList.length) {
            isRedirect = true;
        }

        this.updateParentList(parent.suiteId, 'bikeRacksList', parent).subscribe(() => {
            if (isRedirect) {
                const url = `/directory/item/${parent.suiteId}/bike-racks`;
                const isCurrentUrl = this.router.url === url;

                if (isCurrentUrl) this.getItemId(parent).subscribe();
                else this.router.navigate([url]);
            }
        });
    }

}

@Injectable()
export class ApiInfoService extends ApiService {

    constructor(public api: ApiClientService,
                public route: ActivatedRoute,
                public router: Router,
                public account: AccountPermissionsService) {
        super(api, route, router, account);
    }

    getItems(suiteId: number): Observable<any> {
        return;
    }

    getItemId(parent: DirectoryItemComponent): Observable<number> {
        return;
    }

    getItem(id: number): Observable<any> {
        const url = `/v1/suites/${id}?expand=suiteUser,suiteUser.user,buzzFields,keyCodeFields,keyFobFields,user`;

        return this.api.get('domainBD', url).map(res => {
                const responseBody = JSON.parse(res._body);
                this.account.addEntitiesPermissions([responseBody], 'bldn');
                return responseBody;
            },
            (error) => this.api.errorHandler(error, 'Failed get Suite'));
    }

    updateItem(id: number, data: any): Observable<any> {
        const url = `/v1/suites/${id}`;

        const body = this.prepareData(data);
        return this.api.patch('domainBD', url, body).map((res) => {
            toastr.success('Unit info was updated!');

            return res;
        });
    }

    prepareData(data: any): any {
        const result: any = {};

        createValueIsSet('emergencyContact');
        createValueIsSet('proportionateShare');
        createValueIsSet('occupancyDate');
        createValueIsSet('buzzFields');
        createValueIsSet('keyCodeFields');
        createValueIsSet('keyFobFields');
        createValueIsSet('powerAttorney');
        createValueIsSet('legalDescription');
        createValueIsSet('rentedOut');
        createValueIsSet('note');
        createValueIsSet('allowedVisitor');
        createValueIsSet('level');
        createValueIsSet('enterPhone');
        createValueIsSet('subName');
        createValueIsSet('fromFiveReceived');
        createValueIsSet('permissionToEnter');
        createValueIsSet('commonElementsFee');

        return result;

        function createValueIsSet(key) {
            result[key] = data[key];
        }
    }

    deleteItem(id: number): Observable<any> {
        return undefined;
    }

    updateView(parent, isRedirect): void {
    }

}

@Injectable()
export class ApiDirectoryItemService {

    constructor(private api: ApiClientService) {
    }

    public getSuite(suiteId): Observable<any> {
        const url = `/v1/suites/${suiteId}?expand=building`;

        return this.api.get('domainBD', url).map(res => JSON.parse(res._body),
            (error) => this.api.errorHandler(error, 'Failed get Suite'));
    }

    public getActivityBySuiteId(suiteId, serviceDomain = 'domainBD'): Observable<any> {
        const url = `/v1/history?suite=${suiteId}&order-by=loggedAt|desc&per-page=10000`;

        return this.api.get(serviceDomain, url).map(res => JSON.parse(res._body),
            (error) => this.api.errorHandler(error, 'Failed get history'));
    }

}



// WEBPACK FOOTER //
// /app/src/app/services/api-directory.service.ts